import { useEffect, useState, useContext, useRef } from "react";
import toast from "react-hot-toast";
import { ClientContext } from "ClientProvider";
import { useLocation } from "react-router-dom";
import PayoutList from "./components/PayoutList";
import AddBatchFile from "./components/AddBatchFile";
import { getPayout } from "api/graphQL_crypto";
import PaymentTable from "./components/PaymentsList";
import { getCryptoPayoutsFilter } from "api/payouts";
import { getAllGatewaysSecond } from "api/graphQL_gateways";
import { getCardType } from "api/graphQL_payments";

export interface PayoutRowObj {
  id: any; createdAt: any; batchId: any; clientId: any; clientName: any;
  gatewayId: any; gatewayName: any; totalTransactions: any; gateway: any;
  totalAmount: any; currency: any; transations: any; type: any;
}

export type PaymentRowObj = {
  id: string; payment_id: string; et_id: string;
  amount: string; crypto_amount: string; fiat_amount: string;
  currency: string; crypto_currency: string; fiat_currency: string;
  gateway_id: string; gateway_name: string; status: string;
  meta_info: any; gateway_response: any; payment_type: string;
  client_name: string; refund_info: string; created_at: string;
  image: string; all_data: any[]; expanded: boolean; payment_history: any;
};

const CryptoPayout = () => {
  const [page, setPage] = useState<number>(1);
  const [batchPage, setBatchPage] = useState<number>(1);
  const [payouts, setPayouts] = useState<PayoutRowObj[]>([]);
  const [totalpage, setTotalPage] = useState<number>(0);
  const [batchTotalpage, setBatchTotalPage] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [batchTotalItems, setBatchTotalItems] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [batchPageSize, setBatchPageSize] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [batchCurrentPage, setBatchCurrentPage] = useState<number>(1);
  const [searchTexts, setSearchTexts] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { singleRoleData, setFilter, filter } = useContext(ClientContext);
  const location = useLocation();
  const [clients, setClients] = useState();
  const [localFilters, setLocalFilters] = useState<any>({});
  const [clearClicked, setClearClick] = useState(0);
  const { activeTab, setActiveTab } = useContext(ClientContext);
  const hasSinglePayoutPermission = singleRoleData?.[0]?.crypto?.value?.view_single_crypto_payout_list;
  const hasBatchPayoutPermission = singleRoleData?.[0]?.crypto?.value?.view_crypto_payout_list;
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>();
  const [payments, setPayments] = useState<PaymentRowObj[]>([]);
  const [value, setValue] = useState(false);
  const { paymentType } = useContext(ClientContext);
  const [curretClient, setCurrentClient] = useState<string>("all");
  const [allGateways, setAllGateways] = useState();
  const [allCardType, setCardType] = useState();
  const initialRender = useRef(true);
  const isResetting = useRef(false);

  const fetchPayouts = () => {
    let finalSearchText = "";
    if (location.state?.search) {
      finalSearchText = location.state.search;
      window.history.replaceState({}, document.title);
    } else {
      finalSearchText = searchTexts;
    }
    if (batchPageSize === null || batchPageSize === undefined) { setIsLoading(false); return; }
    setIsLoading(true);
    getPayout({ page: batchPage, per_page: batchPageSize, searchTexts: finalSearchText })
      .then((data: any) => {
        if (data.status === "Error") {
          setPayouts([]); setBatchTotalPage(1); setBatchTotalItems(0); setBatchCurrentPage(1);
        } else {
          setPayouts(data?.[0]);
          setBatchTotalPage(data?.[1].total_pages);
          setBatchTotalItems(data?.[1].total_items);
          setBatchCurrentPage(data?.[1].current_page);
        }
      })
      .catch(() => { setPayouts([]); setIsLoading(false); })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (activeTab === "batch" || (!hasSinglePayoutPermission && hasBatchPayoutPermission)) fetchPayouts();
  }, [batchPage, batchPageSize, searchTexts, clearClicked, activeTab]);

  useEffect(() => {
    isResetting.current = true;
    setPage(1); setBatchPage(1); setPageSize(50); setBatchPageSize(50); setValue(!value);
  }, [paymentType, filter?.status, searchTexts, activeTab]);

  const filterData = () => {
    setIsPaymentLoading(true);
    getCryptoPayoutsFilter(page, pageSize, searchTexts)
      .then((data) => {
        setPayments(data.data); setTotalPage(data.total_pages);
        setTotalItems(data.total_items); setCurrentPage(data.current_page);
      })
      .catch((err) => toast.error(err?.response?.data?.message ?? "Something went wrong"))
      .finally(() => setIsPaymentLoading(false));
  };

  const fetchAllGateways = () => {
    getAllGatewaysSecond().then(setAllGateways).catch((err) => toast.error(err?.response?.data?.message ?? "Error"));
  };
  const fetchCardType = () => {
    getCardType().then(setCardType).catch((err) => toast.error(err?.response?.data?.message ?? "Error"));
  };

  useEffect(() => {
    if (initialRender.current) { initialRender.current = false; return; }
    if (isResetting.current) { isResetting.current = false; return; }
    if (page !== 0 && activeTab === "single") filterData();
  }, [page, curretClient, pageSize, value, clearClicked, activeTab, searchTexts]);

  useEffect(() => { fetchAllGateways(); fetchCardType(); filterData(); }, []);

  const serchboxValueChange = async (searchTerm: string) => {
    const search_txt = searchTerm || "";
    if (search_txt !== searchTexts && searchTexts !== "") {
      await setSearchTexts(search_txt); setPage(1); setCurrentPage(1);
    } else {
      await setSearchTexts(search_txt);
    }
  };

  const onClearClick = () => setClearClick((prev) => prev + 1);

  const sharedPaymentTableProps = {
    pageSize, setPageSize, tableData: payments, fetchPayments: filterData,
    isLoading: isPaymentLoading, page, setPage, totalpage, totalItems,
    currentPage, clients, allGateways, allCardType, curretClient,
    setCurrentClient, roleData: singleRoleData, filterData,
    setClearClick: onClearClick, filter, setFilter,
    onValueChange: serchboxValueChange, setLocalFilters,
  };

  const sharedPayoutListProps = {
    tableData: payouts, fetchPayouts, isLoading, roleData: singleRoleData,
    onValueChange: serchboxValueChange, page: batchPage, setPage: setBatchPage,
    totalpage: batchTotalpage, totalItems: batchTotalItems,
    currentPage: batchCurrentPage, pageSize: batchPageSize,
    setPageSize: setBatchPageSize, setLocalFilters, filterData: fetchPayouts,
    localFilters, filter, setFilter, setClearClick: onClearClick,
  };

  return (
    <div className="cp-root">
      {/* Page header */}
      <div className="cp-page-header">
        <div className="cp-title-block">
          <div className="cp-page-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <div>
            <h1 className="cp-heading">Crypto Payout</h1>
            <p className="cp-sub">Manage single and batch cryptocurrency payout transactions</p>
          </div>
        </div>
      </div>

      {hasSinglePayoutPermission && hasBatchPayoutPermission ? (
        <>
          {/* Toggle tabs */}
          <div className="cp-tab-wrap">
            <div className="cp-tab-track">
              <div
                className="cp-tab-slider"
                style={{ transform: activeTab === "single" ? "translateX(0%)" : "translateX(100%)" }}
              />
              <button
                onClick={() => setActiveTab("single")}
                className={`cp-tab-btn${activeTab === "single" ? " cp-tab-btn--active" : ""}`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                Single Payout
              </button>
              <button
                onClick={() => setActiveTab("batch")}
                className={`cp-tab-btn${activeTab === "batch" ? " cp-tab-btn--active" : ""}`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
                Batch Payout
              </button>
            </div>
          </div>

          {activeTab === "single" && <PaymentTable {...sharedPaymentTableProps} />}
          {activeTab === "batch" && (
            <>
              {/* <div className="cp-batch-add-grid">
                <AddBatchFile fetchPayouts={fetchPayouts} />
              </div> */}
              <PayoutList {...sharedPayoutListProps} />
            </>
          )}
        </>
      ) : hasSinglePayoutPermission ? (
        <PaymentTable {...sharedPaymentTableProps} />
      ) : hasBatchPayoutPermission ? (
        <>
          {/* <div className="cp-batch-add-grid">
            <AddBatchFile fetchPayouts={fetchPayouts} />
          </div> */}
          <PayoutList {...sharedPayoutListProps} />
        </>
      ) : null}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .cp-root { font-family:'Plus Jakarta Sans',sans-serif; display:flex; flex-direction:column; gap:20px; }
        .cp-page-header { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:16px; }
        .cp-title-block { display:flex; align-items:center; gap:14px; }
        .cp-page-icon { width:44px; height:44px; border-radius:12px; background:linear-gradient(135deg,#faf5ff,#ede9fe); border:1.5px solid #ddd6fe; display:flex; align-items:center; justify-content:center; color:#7c3aed; flex-shrink:0; }
        .cp-heading { font-size:20px; font-weight:800; color:#0f172a; letter-spacing:-0.4px; margin-bottom:2px; line-height:1.2; }
        .cp-sub { font-size:13px; color:#94a3b8; font-weight:500; }
        .cp-tab-wrap { display:flex; }
        .cp-tab-track { position:relative; display:flex; background:#f1f5f9; border:1.5px solid #e2e8f0; border-radius:12px; padding:4px; gap:0; }
        .cp-tab-slider { position:absolute; top:4px; bottom:4px; width:calc(50% - 4px); background:#fff; border-radius:9px; box-shadow:0 2px 8px rgba(15,23,42,.1); transition:transform .25s cubic-bezier(0.16,1,0.3,1); pointer-events:none; }
        .cp-tab-btn { position:relative; display:inline-flex; align-items:center; gap:7px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:600; color:#64748b; padding:10px 24px; border:none; background:none; border-radius:9px; cursor:pointer; transition:color .2s; white-space:nowrap; }
        .cp-tab-btn--active { color:#7c3aed; font-weight:700; }
        .cp-batch-add-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(240px,1fr)); gap:16px; }
      `}</style>
    </div>
  );
};

export default CryptoPayout;